import React, { useState, useEffect, useCallback } from 'react'
import { Container, Row, Col, Card, CardBody, Spinner, Alert } from 'reactstrap';
import ViolatedCompliance from './ViolatedCompliance';
import ComplianceTable from './ComplianceTable';
import ComplianceFramework from './ComplianceFramework';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Orange color palette
const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const cardShell = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: "10px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const pageHeadingStyle = { color: "var(--vz-body-color)" };
const toRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
const pageIconChipStyle = (color) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  border: `0.5px solid ${toRgba(color, 0.30)}`,
  background: toRgba(color, 0.10),
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});
const pageIconGlyphStyle = (color) => ({ color, fontSize: 18 });


const CACHE_DURATION = 5 * 60 * 1000;
let complianceCache = null;


const MetricTile = ({ label, value, icon, color, borderColor }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card 
      className="card-animate" 
      style={{ 
        ...cardShell,
        border: "1px solid var(--vz-border-color)",
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 8px 16px rgba(0,0,0,0.08)' : cardShell.boxShadow,
        cursor: 'pointer',
        minHeight: '140px',
        height: '100%'
      }}
      onMouseEnter={() => setIsHovered(true)} 
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardBody style={{ 
        display: 'flex', 
        flexDirection: 'column',
        height: '100%'
      }}>
        <div className="d-flex align-items-center">
          <div className="flex-grow-1">
            <p className="text-uppercase fw-medium text-muted mb-0" style={{ 
              fontSize: '11px',
              letterSpacing: '0.5px'
            }}>
              {label}
            </p>
          </div>
          <div className="flex-shrink-0">
            <div style={pageIconChipStyle(color)}>
              <div style={pageIconGlyphStyle(color)}>
              {icon}
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex align-items-end justify-content-between mt-auto pt-3">
          <div>
            <h4 
              className="fs-22 fw-semibold ff-secondary mb-0" 
              style={{ color: color }}
            >
              {value !== null && value !== undefined ? value.toLocaleString() : '0'}
            </h4>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

function ComplianceShield() {
  const [compliance, setCompliance] = useState({
    total_mappings: 0,
    unique_threats: 0,
    unique_controls: 0,
    frameworks_covered: 0
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = useCallback(() => {
    const authUser = JSON.parse(sessionStorage.getItem("authUser"));
    return {
      "Content-Type": "application/json",
      ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
    };
  }, []);

  useEffect(() => {
    const fetchCompliance = async () => {
      try {
     
        if (complianceCache && Date.now() - complianceCache.timestamp < CACHE_DURATION) {
          console.log(' Using cached compliance data');
          setCompliance(complianceCache.data);
          setLoading(false);
          return;
        }

        console.log(' Fetching compliance summary...');
        const headers = getAuthHeaders();
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        
        // Use unified endpoint with mode=summary
        const res = await fetch(`${API_BASE_URL}/threats/compliance-mappings?mode=summary`, { 
          headers,
          signal: controller.signal 
        });
        
        clearTimeout(timeoutId);
        
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        
        const data = await res.json();
        console.log(' Compliance Summary Response:', data);

        if (data.success && data.data) {
          const complianceData = {
            total_mappings: data.data.total_mappings || 0,
            unique_threats: data.data.unique_threats || 0,
            unique_controls: data.data.unique_controls || 0,
            frameworks_covered: data.data.frameworks_covered || 0
          };

          setCompliance(complianceData);
          
          // Cache the data
          complianceCache = {
            data: complianceData,
            timestamp: Date.now()
          };
          
          console.log(' Compliance stats:', complianceData);
        } else {
          console.warn(' No compliance data available');
          setCompliance({
            total_mappings: 0,
            unique_threats: 0,
            unique_controls: 0,
            frameworks_covered: 0
          });
          setError('No compliance data available');
        }
      } catch (err) {
        console.error('Error fetching compliance:', err);
        if (err.name === 'AbortError') {
          setError('Request timed out. Please try again.');
        } else {
          setError(err.message || 'Failed to load compliance data');
        }
        
        setCompliance(complianceCache?.data || {
          total_mappings: 0,
          unique_threats: 0,
          unique_controls: 0,
          frameworks_covered: 0
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCompliance();
  }, [getAuthHeaders]);

  if (loading && !complianceCache) {
    return (
      <div className='page-content'>
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
            <Spinner style={{ color: ORANGE_COLORS.primary, width: "3rem", height: "3rem" }} />
          </div>
        </Container>
      </div>
    );
  }

  const tileConfig = [
    {
      label: "Total Mappings",
      value: compliance.total_mappings,
      icon: <i className="ri-shield-line" />,
      color: ORANGE_COLORS.primary,
      borderColor: 'var(--vz-border-color)'
    },
    {
      label: "Unique Threats",
      value: compliance.unique_threats,
      icon: <i className="ri-alert-line" />,
      color: ORANGE_COLORS.dark,
      borderColor: 'var(--vz-border-color)'
    },
    {
      label: "Unique Controls",
      value: compliance.unique_controls,
      icon: <i className="ri-shield-check-line" />,
      color: ORANGE_COLORS.darker,
      borderColor: 'var(--vz-border-color)'
    },
    {
      label: "Frameworks Covered",
      value: compliance.frameworks_covered,
      icon: <i className="ri-shield-star-line" />,
      color: ORANGE_COLORS.light,
      borderColor: 'var(--vz-border-color)'
    }
  ];

  return (
    <div className='page-content'>
      <Container fluid>
        {/* Page Title */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex align-items-center gap-3">
              <div style={pageIconChipStyle(ORANGE_COLORS.primary)}>
                <i className="ri-shield-check-line" style={pageIconGlyphStyle(ORANGE_COLORS.primary)}></i>
              </div>
              <h4 className="mb-0" style={pageHeadingStyle}>
                Compliance Dashboard
              </h4>
            </div>
          </Col>
        </Row>

        {/* Error Alert */}
        {error && (
          <Row className="mb-3">
            <Col>
              <Alert style={{ backgroundColor: ORANGE_COLORS.subtle, color: ORANGE_COLORS.darker, border: `1px solid ${ORANGE_COLORS.muted}` }}>
                <i className="ri-alert-line me-2 fs-5"></i>
                <div>
                  {error}
                  <div className="mt-1 small">
                    Showing cached or fallback values.
                  </div>
                </div>
              </Alert>
            </Col>
          </Row>
        )}

        {/* Stats Cards */}
        <Row className="g-3 mb-4">
          {tileConfig.map((tile, idx) => (
            <Col xl={3} md={6} sm={6} xs={12} key={idx}>
              <MetricTile {...tile} />
            </Col>
          ))}
        </Row>

        {/* Charts Row */}
        <ViolatedCompliance />

        {/* Framework Coverage */}
        <ComplianceFramework />

        {/* Compliance Table */}
        <Row className="mt-4">

          <Col>
            <ComplianceTable />
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default ComplianceShield;
